import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PanelTemplate from "../../templates/Panel";
import { hasPermission, hasPlanFeature } from "../../services/auth";
import { renderError } from "../../helpers/errors";
import api from "../../services/api";
import SuppliersTab from "./tabs/SuppliersTab";
import FeedsTab from "./tabs/FeedsTab";
import ExternalApi from "../ExternalApi";
import Webhook from "../Webhook";
import { FEED_CHANNELS } from "../Feed/feeds.config";
import "./styles.scss";

const svgProps = {
    width: 19,
    height: 19,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

const TAB_ICONS = {
    fornecedores: (
        <svg {...svgProps}>
            <path d="M12 2 3 7v10l9 5 9-5V7l-9-5Z" />
            <path d="M3 7l9 5 9-5" />
            <path d="M12 22V12" />
        </svg>
    ),
    feeds: (
        <svg {...svgProps}>
            <path d="M4 11a9 9 0 0 1 9 9" />
            <path d="M4 4a16 16 0 0 1 16 16" />
            <circle cx="5" cy="19" r="1.5" fill="currentColor" stroke="none" />
        </svg>
    ),
    api: (
        <svg {...svgProps}>
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
        </svg>
    ),
    webhook: (
        <svg {...svgProps}>
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
    ),
};

const IntegrationList = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(true);

    useEffect(() => {
        setLoadingSuppliers(true);
        api.get(`/supplier`).then(res => {
            const activeSuppliers = (res.data?.resources?.data ?? []).filter(
                (supplier) => supplier?.is_active !== false
            );
            setSuppliers(activeSuppliers);
        }).catch(() => {
            renderError("Falha ao carregar Fornecedores");
        }).then(() => setLoadingSuppliers(false));
    }, []);

    const tabs = [
        {
            key: "fornecedores",
            label: "Fornecedores",
            meta: loadingSuppliers ? "Carregando…" : `${suppliers.length} conectados`,
            color: "#362B7A",
            bg: "#EEEDFE",
            show: true,
            content: <SuppliersTab suppliers={suppliers} loading={loadingSuppliers} />,
        },
        {
            key: "feeds",
            label: "Feeds de catálogo",
            meta: `${FEED_CHANNELS.length} canais`,
            color: "#0F6E56",
            bg: "#E1F5EE",
            show: true,
            content: <FeedsTab />,
        },
        {
            key: "api",
            label: "API",
            meta: "Chave de acesso",
            color: "#185FA5",
            bg: "#E6F1FB",
            show: hasPermission("external_api"),
            content: <ExternalApi embedded />,
        },
        {
            key: "webhook",
            label: "Webhook",
            meta: "Notificações de eventos",
            color: "#854F0B",
            bg: "#FAEEDA",
            show: hasPermission("webhook"),
            content: <Webhook embedded />,
        },
    ].filter((tab) => tab.show);

    const requested = searchParams.get("tab");
    const active = tabs.some((tab) => tab.key === requested) ? requested : tabs[0].key;

    const handleTab = (key) => {
        setSearchParams(key === tabs[0].key ? {} : { tab: key });
    };

    const activeTab = tabs.find((tab) => tab.key === active);

    return (
        <PanelTemplate id="integration-list">
            <div className="integration-page">
                <h1>Integrações</h1>
                <h3>
                    Conecte fornecedores, exponha seu catálogo e integre sistemas externos em um só lugar.
                </h3>

                <div className="integration-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            className={`integration-tab${tab.key === active ? " active" : ""}`}
                            onClick={() => handleTab(tab.key)}
                        >
                            <span
                                className="integration-tab__icon"
                                style={{ background: tab.bg, color: tab.color }}
                            >
                                {TAB_ICONS[tab.key]}
                            </span>
                            <span className="integration-tab__text">
                                <span className="integration-tab__label">{tab.label}</span>
                                <span className="integration-tab__meta">{tab.meta}</span>
                            </span>
                        </button>
                    ))}
                </div>

                <div className="integration-tab-content">
                    {activeTab?.content}
                </div>
            </div>
        </PanelTemplate>
    );
};

export default IntegrationList;
